import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, type OrderRow, type OrderStatus } from "@/lib/api";
import { getItem, formatPrice } from "@/lib/donation-items";

const statusOptions = ["", "pending", "acknowledged", "paid", "fulfilled", "cancelled", "archived"] as const;

function itemLabel(t: OrderRow["item_type"]): string {
  return getItem(t)?.shortName ?? getItem(t)?.name ?? t;
}

function engravingSummary(r: OrderRow): string {
  if (r.engraving_lines) {
    try {
      const lines = JSON.parse(r.engraving_lines) as string[];
      const joined = lines.filter((l) => l && l.trim() !== "").join(" / ");
      if (joined) return joined;
    } catch {
      /* fall through */
    }
  }
  if (r.plaque_name) return r.plaque_name;
  if (r.design_description) return r.design_description;
  return "—";
}

export default function AdminOrders() {
  const [status, setStatus] = useState<string>("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<{ rows: OrderRow[]; total: number; pageSize: number } | null>(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const r = await api.listOrders({ status, q, page: String(page), pageSize: "25" });
      setData(r);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, page]);

  return (
    <div>
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div>
          <label className="label">Status</label>
          <select className="select" value={status}
                  onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
            {statusOptions.map((s) => (
              <option key={s} value={s}>{s || "all"}</option>
            ))}
          </select>
        </div>
        <div className="grow min-w-[200px]">
          <label className="label">Search</label>
          <input className="input" placeholder="purchaser, email, local #"
                 value={q} onChange={(e) => setQ(e.target.value)}
                 onKeyDown={(e) => { if (e.key === "Enter") load(); }} />
        </div>
        <button className="btn-secondary" onClick={load}>Search</button>
      </div>

      <div className="card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-left">
            <tr>
              <th className="px-3 py-2">#</th>
              <th className="px-3 py-2">Submitted</th>
              <th className="px-3 py-2">Item</th>
              <th className="px-3 py-2">Purchaser</th>
              <th className="px-3 py-2">Engraving</th>
              <th className="px-3 py-2">Contribution</th>
              <th className="px-3 py-2">Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={8} className="px-3 py-6 text-museum-muted">Loading…</td></tr>
            )}
            {!loading && data?.rows.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-6 text-museum-muted">No donations match.</td></tr>
            )}
            {!loading && data?.rows.map((r) => (
              <tr key={r.id} className="border-t border-stone-200 align-top">
                <td className="px-3 py-2">{r.id}</td>
                <td className="px-3 py-2 text-museum-muted whitespace-nowrap">
                  {new Date(r.created_at + "Z").toLocaleString()}
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{itemLabel(r.item_type)}</div>
                  <div className="text-xs text-museum-muted">
                    {r.with_logo ? "with logo" : "no logo"}
                    {r.local_number ? ` · Local #${r.local_number}` : ""}
                    {r.custom_logo_requested ? " · custom logo" : ""}
                  </div>
                </td>
                <td className="px-3 py-2">
                  <div className="font-medium">{r.purchaser_name}</div>
                  <div className="text-xs text-museum-muted">{r.contact_email}</div>
                  <div className="text-xs text-museum-muted">{r.contact_phone}</div>
                </td>
                <td className="px-3 py-2 max-w-[240px]">
                  <span className="whitespace-pre-wrap break-words">{engravingSummary(r)}</span>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">{formatPrice(r.total_cents)}</td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    <StatusBadge status={r.status} />
                    {r.archived_at && (
                      <span className="inline-block px-2 py-0.5 rounded text-xs bg-stone-200 text-stone-700">Archived</span>
                    )}
                  </div>
                </td>
                <td className="px-3 py-2 text-right">
                  <Link to={`/admin/donations/${r.id}`} className="underline">View</Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data && data.total > data.pageSize && (
        <div className="flex justify-between items-center mt-3 text-sm">
          <div>{data.total} total</div>
          <div className="flex gap-2">
            <button className="btn-secondary" disabled={page === 1}
                    onClick={() => setPage((p) => p - 1)}>← Prev</button>
            <button className="btn-secondary"
                    disabled={page * data.pageSize >= data.total}
                    onClick={() => setPage((p) => p + 1)}>Next →</button>
          </div>
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: OrderStatus }) {
  const cls: Record<OrderStatus, string> = {
    pending:      "bg-amber-100 text-amber-800",
    acknowledged: "bg-sky-100 text-sky-800",
    paid:         "bg-green-100 text-green-800",
    fulfilled:    "bg-emerald-100 text-emerald-800",
    cancelled:    "bg-stone-200 text-stone-700",
  };
  return <span className={`inline-block px-2 py-0.5 rounded text-xs ${cls[status]}`}>{status}</span>;
}
